// external
import { useState } from "react";
import classNames from "classnames";

// components
import Heading from "components/partials/heading/heading";
import ToggleSwitch from "components/partials/ToggleSwitch";

// helpers
import {
  renderInputCell,
  renderRegularCell,
  renderTableHeader,
} from "./helpers/results-table-renderers";

/**
 * Default configuration objects for optional props
 * These provide safe defaults when specific functionality isn't needed
 */
const DEFAULT_EDITABLE = {
  isEditable: false,
  inputCells: [],
  handleInputChange: () => {},
};

const DEFAULT_RESET = {
  resetToggle: false,
  handleReset: () => {},
  handleResetAll: () => {},
  isPopulationAdjusted: () => {},
  isGroupAdjusted: () => {},
};

const DEFAULT_LAST_YEAR = {
  lastYearToggle: false,
  lastYearData: [],
};

/**
 * AidResultsTable Component
 *
 * A highly configurable table component for displaying FAO aid results data.
 * Supports multiple display modes and interaction patterns:
 *
 * Core Features:
 * - Responsive table layout with proper borders and spacing
 * - Conditional row highlighting for modified data
 * - Flexible header configuration with custom content
 * - Empty state handling for missing data
 *
 * Optional Features (via props):
 * - Editable cells with input validation and change handling
 * - Reset functionality for individual rows and all data
 * - Last year comparison with toggle visibility
 * - Custom heading with consistent styling
 *
 * Configuration Props:
 * @param {Object} displayData - Core table data and headers
 * @param {Object} editable - Optional editable functionality config
 * @param {Object} reset - Optional reset functionality config
 * @param {Object} lastYear - Optional last year comparison config
 *
 */
function AidResultsTable({
  displayData,
  editable = DEFAULT_EDITABLE,
  reset = DEFAULT_RESET,
  lastYear = DEFAULT_LAST_YEAR,
}) {
  // Extract configuration from props with defaults
  const { lastYearToggle, lastYearData } = lastYear;
  const { isEditable, inputCells, handleInputChange } = editable;
  const { resetToggle, handleReset, handleResetAll, isGroupAdjusted } = reset;
  const { tableHeaders, tableData, heading = "" } = displayData;

  // Local state for last year comparison toggle
  const [showLastYear, setShowLastYear] = useState(lastYearToggle);

  /**
   * Early return for invalid or empty data
   * Provides user feedback when table cannot be rendered
   */
  if (!tableData || !tableHeaders || tableData.length === 0) {
    return (
      <div className="mt-4 text-center text-gray-04">
        No data available to display
      </div>
    );
  }

  /**
   * Main cell rendering function that determines the appropriate renderer
   *
   * Logic:
   * 1. Check if cell should be an input (based on inputCells config)
   * 2. Use input renderer with change handling if editable
   * 3. Otherwise use regular cell renderer with formatting
   *
   * @param {Object} cellProps - Properties needed for cell rendering
   * @returns {JSX.Element} Rendered table cell
   */
  const renderCell = (cellProps) => {
    const { header } = cellProps;

    // Find matching input configuration for this column
    const input = inputCells.find((cur) => cur.key === header.key);

    if (input) {
      // Render as editable input cell
      return renderInputCell({
        ...cellProps,
        inputStep: input.step,
        tableHeaders,
        handleInputChange,
        showLastYear,
        lastYearData,
      });
    }

    // Render as regular display cell
    return renderRegularCell({
      ...cellProps,
      tableHeaders,
      resetToggle,
      isEditable,
      handleReset,
    });
  };

  /**
   * Renders the component header section with optional title and toggle
   * Layout adapts based on whether heading text is provided
   */
  const renderComponentHeader = () => (
    <div
      className={classNames("flex", {
        "justify-end": !heading, // Right-align when no heading
        "justify-between": heading, // Space between when heading exists
      })}
    >
      {/* Optional table heading */}
      {heading && (
        <Heading variant="h3" customClass="font-semibold text-gray-05">
          {heading}
        </Heading>
      )}

      {/* Last year comparison toggle */}
      {lastYearToggle && (
        <ToggleSwitch
          label="See Last Year (Value)"
          labelCssName="text-gray-03"
          checked={showLastYear}
          onChange={() => setShowLastYear(!showLastYear)}
        />
      )}
    </div>
  );

  /**
   * Renders the main data table with headers and body
   * Includes proper styling for borders, spacing, and highlighting
   */
  const renderTable = () => (
    <table className="h-full w-full table-fixed border-collapse">
      {/* Table header with column titles and optional reset controls */}
      <thead className="bg-gray-01">
        <tr className="border-b">
          {tableHeaders.map((item, index) => (
            <th
              key={`header-${index}`}
              className={classNames("p-2 text-left font-normal text-blue-02", {
                "border-r": tableHeaders.length - 1 !== index, // Right border except last column
              })}
            >
              {renderTableHeader(item, resetToggle, handleResetAll)}
            </th>
          ))}
        </tr>
      </thead>

      {/* Table body with data rows */}
      <tbody>
        {tableData.map((item, index) => (
          <tr
            key={`row-${item.group || item.id || index}`} // Flexible key generation
            className={classNames({
              "border-b": tableData.length - 1 !== index, // Bottom border except last row
              "bg-green-02": isGroupAdjusted(item), // Highlight modified groups
            })}
          >
            {/* Render each cell in the row */}
            {tableHeaders.map((header, headerIndex) =>
              renderCell({ header, headerIndex, item })
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className={classNames({ "mt-2": !heading, "mt-4": heading })}>
      {/* Component header with optional title and controls */}
      {renderComponentHeader()}

      {/* Main table container with border and scroll handling */}
      <div className="mt-1 overflow-hidden rounded-md border">
        {renderTable()}
      </div>
    </div>
  );
}

export default AidResultsTable;
